import { z } from 'zod';

// Base Region Type
export interface Region {
  id: number;
  provider_id: number;
  name: string;
  zip_code?: string | null; // Optional zip code
  country_code?: string | null; // Enhanced geographic field
  state_province?: string | null; // Enhanced geographic field
  city_coverage?: string[] | null; // Enhanced geographic field
  rural_availability?: boolean | null; // Enhanced geographic field
  created_at: string;
  updated_at: string;
}

// Region with related data
export interface RegionWithCounts extends Region {
  _count: {
    versions: number;
  };
}

// Schema for region creation
export const createRegionSchema = z.object({
  name: z.string()
    .min(2, { message: 'Region name must be at least 2 characters long' })
    .max(50, { message: 'Region name cannot exceed 50 characters' })
    .regex(/^[a-zA-Z0-9 .\-_]+$/, { 
      message: 'Region name can only contain letters, numbers, spaces, and .-_' 
    }),
  provider_id: z.number().positive({ message: 'Provider ID is required' }),
  zip_code: z.string()
    .max(20, { message: 'Zip code cannot exceed 20 characters' })
    .regex(/^[a-zA-Z0-9\-\s]*$/, { message: 'Zip code can only contain letters, numbers, hyphens, and spaces' })
    .optional()
    .nullable(),
  country_code: z.string()
    .max(10, { message: 'Country code cannot exceed 10 characters' })
    .regex(/^[A-Z]{2,3}$/, { message: 'Country code must be 2-3 uppercase letters (e.g., US, GB)' })
    .optional()
    .nullable(),
  state_province: z.string()
    .max(100, { message: 'State/Province cannot exceed 100 characters' })
    .optional()
    .nullable(),
  city_coverage: z.array(z.string().max(50, { message: 'City name cannot exceed 50 characters' }))
    .max(20, { message: 'Cannot specify more than 20 cities' })
    .optional()
    .nullable(),
  rural_availability: z.boolean()
    .optional()
    .nullable()
});

// Type for region creation
export type CreateRegionInput = z.infer<typeof createRegionSchema>;

// Schema for region updates
export const updateRegionSchema = z.object({
  name: z.string()
    .min(2, { message: 'Region name must be at least 2 characters long' })
    .max(50, { message: 'Region name cannot exceed 50 characters' })
    .regex(/^[a-zA-Z0-9 .\-_]+$/, { 
      message: 'Region name can only contain letters, numbers, spaces, and .-_' 
    }).optional(),
  zip_code: z.string()
    .max(20, { message: 'Zip code cannot exceed 20 characters' })
    .regex(/^[a-zA-Z0-9\-\s]*$/, { message: 'Zip code can only contain letters, numbers, hyphens, and spaces' })
    .optional()
    .nullable()
    .transform(val => val === '' ? null : val), // Convert empty string to null
  country_code: z.string()
    .max(10, { message: 'Country code cannot exceed 10 characters' })
    .regex(/^[A-Z]{2,3}$/, { message: 'Country code must be 2-3 uppercase letters (e.g., US, GB)' })
    .optional()
    .nullable()
    .transform(val => val === '' ? null : val),
  state_province: z.string()
    .max(100, { message: 'State/Province cannot exceed 100 characters' })
    .optional()
    .nullable()
    .transform(val => val === '' ? null : val),
  city_coverage: z.array(z.string().max(50, { message: 'City name cannot exceed 50 characters' }))
    .max(20, { message: 'Cannot specify more than 20 cities' })
    .optional()
    .nullable(),
  rural_availability: z.boolean()
    .optional()
    .nullable()
});

// Type for region updates
export type UpdateRegionInput = z.infer<typeof updateRegionSchema>;

// Service Error Types
export enum RegionErrorCodes {
  CREATE_ERROR = 'CREATE_ERROR',
  UPDATE_ERROR = 'UPDATE_ERROR',
  DELETE_ERROR = 'DELETE_ERROR',
  NOT_FOUND = 'NOT_FOUND',
  DUPLICATE_NAME = 'DUPLICATE_NAME',
  HAS_VERSIONS = 'HAS_VERSIONS'
}

export class RegionServiceError extends Error {
  code: RegionErrorCodes;
  cause?: any;

  constructor(
    message: string,
    code: RegionErrorCodes,
    cause?: any
  ) {
    super(message);
    this.name = 'RegionServiceError';
    this.code = code;
    this.cause = cause;
  }
}

// Updated Version Types
export interface CreateVersionWithRegionInput {
  provider_id: number;
  region_id: number;
  description?: string;
  source_version_id?: number;
}

// Update Version Types
export interface UpdateVersionWithRegionInput {
  description?: string;
  status?: string;
  effective_date?: string;
}
